/**
 * State control for the Dose Key Differentiators section. Expanding a card
 * sets the container's data-expanded state (CSS animates the widths) and
 * anchors the track. The nav arrows step the expansion to the previous or
 * next card; dots reflect and set expansion (the open card's dot renders as
 * a dash). Any open card can be toggled closed on both breakpoints; mobile
 * starts with the first card open.
 *
 * Anchoring rides the width transition frame-by-frame: the track has no
 * overflow until the expanding card grows, so a plain scrollTo at click time
 * would clamp to zero and never anchor.
 */
class DoseKeyDifferentiators extends HTMLElement {
  desktopQuery = window.matchMedia('(min-width: 750px)');
  reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.track = this.querySelector('.dose-key-differentiators__track');
    this.addEventListener('click', this.handleClick);
    this.desktopQuery.addEventListener('change', this.handleBreakpointChange);

    if (!this.desktopQuery.matches) {
      this.setExpanded('1');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.desktopQuery.removeEventListener('change', this.handleBreakpointChange);
    cancelAnimationFrame(this.anchorFrame);
  }

  /* Entering mobile with nothing open applies the mobile default (first card
     open); the user can still close it */
  handleBreakpointChange = () => {
    if (!this.desktopQuery.matches && this.dataset.expanded === 'none') {
      this.setExpanded('1');
    }
  };

  handleClick = (event) => {
    const arrow = event.target.closest('.dose-key-differentiators__nav-arrow');

    if (arrow && this.contains(arrow)) {
      this.stepExpansion(arrow.dataset.direction);
      return;
    }

    const dot = event.target.closest('.dose-key-differentiators__dot');

    if (dot && this.contains(dot)) {
      this.setExpanded(dot.dataset.target);
      return;
    }

    const toggle = event.target.closest('.dose-key-differentiators__toggle');
    if (!toggle || !this.contains(toggle)) return;

    if (this.dataset.expanded === toggle.dataset.card) {
      this.setExpanded('none');
      return;
    }

    this.setExpanded(toggle.dataset.card);
  };

  /* Close the current card and open its neighbor */
  stepExpansion(direction) {
    const current = parseInt(this.dataset.expanded, 10);
    if (!current) return;

    const next = direction === 'prev' ? current - 1 : current + 1;

    if (this.querySelector(`.dose-key-differentiators__card[data-card="${next}"]`)) {
      this.setExpanded(String(next));
    }
  }

  /* One collapsed card slot plus the flex gap — the unit anchoring moves by */
  slotWidth() {
    if (!this.track) return 0;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return (this.track.clientWidth - 2 * gap) / 3 + gap;
  }

  setExpanded(value) {
    this.dataset.expanded = value;

    for (const card of this.querySelectorAll('.dose-key-differentiators__card')) {
      const open = card.dataset.card === value;
      card.toggleAttribute('data-open', open);
      card.querySelector('.dose-key-differentiators__toggle')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    for (const dot of this.querySelectorAll('.dose-key-differentiators__dot')) {
      dot.toggleAttribute('data-dash', dot.dataset.target === value);
    }

    const nav = this.querySelector('.dose-key-differentiators__nav');
    if (nav) nav.hidden = value === 'none';

    if (this.desktopQuery.matches) {
      /* Cards 1 and 2 anchor to the left edge; card 3 right-anchors — the
         same one-slot offset, since expanded + one neighbor fills the row */
      const target = value === '2' || value === '3' ? this.slotWidth() : 0;
      this.anchorTo(target);
    }

    this.updateArrows();
  }

  anchorTo(target) {
    if (!this.track) return;

    cancelAnimationFrame(this.anchorFrame);

    if (this.reducedMotionQuery.matches) {
      this.track.scrollLeft = target;
      return;
    }

    if (target <= this.track.scrollLeft) {
      this.track.scrollTo({ left: target, behavior: 'smooth' });
      return;
    }

    const started = performance.now();

    const step = (now) => {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollLeft = Math.min(target, maxScroll);

      if (this.track.scrollLeft < target - 0.5 && now - started < 1500) {
        this.anchorFrame = requestAnimationFrame(step);
      }
    };

    this.anchorFrame = requestAnimationFrame(step);
  }

  /* Arrows step expansion, so their state follows the open card's position */
  updateArrows() {
    const current = parseInt(this.dataset.expanded, 10) || 0;
    const prev = this.querySelector('[data-direction="prev"]');
    const next = this.querySelector('[data-direction="next"]');

    if (prev) prev.disabled = current <= 1;
    if (next) next.disabled = !this.querySelector(`.dose-key-differentiators__card[data-card="${current + 1}"]`);
  }
}

if (!customElements.get('dose-key-differentiators')) {
  customElements.define('dose-key-differentiators', DoseKeyDifferentiators);
}
